import { Diagnostic } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DiagnosticRef } from "../utils/prepareRuleForImport";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { getDiagnostics } from "@/hooks/diagnostic/useDiagnosticsBySearch";

interface Props {
  open: boolean;
  ruleName: string;
  missingDiagnostics: DiagnosticRef[];
  onResolve: (resolutions: Map<string, string>) => void;
  onSkip: () => void;
}

export default function DiagnosticResolverDialog({
  open,
  ruleName,
  missingDiagnostics,
  onResolve,
  onSkip,
}: Props) {
  const { t } = useTranslation();
  const { geotabClient } = useGeotabClient();

  // Which missing diagnostic we're currently resolving (index into missingDiagnostics)
  const [currentIdx, setCurrentIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Diagnostic[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [resolutions] = useState(() => new Map<string, string>());

  const current = missingDiagnostics[currentIdx];

  const handleSearch = async () => {
    if (search.trim().length === 0) return;
    setSearching(true);
    setSelected(null);
    try {
      const diags = await getDiagnostics(geotabClient, search, undefined, 50);
      setResults(diags);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (!selected || !current) return;
    resolutions.set(current.id, selected);

    if (currentIdx + 1 < missingDiagnostics.length) {
      // Move to next missing diagnostic
      setCurrentIdx(currentIdx + 1);
      setSearch("");
      setResults([]);
      setSelected(null);
    } else {
      // All resolved
      onResolve(resolutions);
      reset();
    }
  };

  const handleSkip = () => {
    onSkip();
    reset();
  };

  const reset = () => {
    setCurrentIdx(0);
    setSearch("");
    setResults([]);
    setSelected(null);
    resolutions.clear();
  };

  if (!current) return null;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("admin.rules.resolveDiagnostic")}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("admin.rules.diagnosticMissing", {
            rule: ruleName,
            current: currentIdx + 1,
            total: missingDiagnostics.length,
          })}
        </Alert>

        <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            {t("admin.rules.diagnosticId")}
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
            {current.id}
          </Typography>
          {current.unitOfMeasure && (
            <>
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                {t("admin.rules.unitOfMeasure")}
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {current.unitOfMeasure}
              </Typography>
            </>
          )}
          <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
            {t("admin.rules.location")}
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {current.path}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={t("admin.rules.searchDiagnostic")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={searching || search.trim().length === 0}
            sx={{ flexShrink: 0 }}
          >
            {t("search.title")}
          </Button>
        </Box>

        {results.length > 0 && (
          <List
            dense
            sx={{
              maxHeight: 250,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {results.map(d => (
              <ListItemButton
                key={d.id}
                selected={selected === d.id}
                onClick={() => setSelected(d.id)}
              >
                <ListItemText
                  primary={d.name}
                  secondary={d.id}
                  slotProps={{ secondary: { fontFamily: "monospace", fontSize: "0.75rem" } }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {results.length === 0 && search.length > 0 && !searching && (
          <Typography variant="body2" color="text.secondary">
            {t("admin.rules.noResults")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSkip} color="inherit">
          {t("admin.rules.skipRule")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selected}
        >
          {currentIdx + 1 < missingDiagnostics.length
            ? t("admin.rules.confirmNext")
            : t("admin.rules.confirmImport")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
